import { Search } from "lucide-react";
import { useState } from "react";
import { EmptyState, Spinner } from "../../components/ui/misc";
import { Panel } from "../../components/ui/Panel";
import { Select } from "../../components/ui/Select";
import type { ItemCategory, ItemFilter } from "../../lib/api/models";
import { useItems, useSites } from "../../lib/queries";
import { useDebounced } from "../search/useDebounced";
import { ItemsTable } from "./ItemsTable";

/** Devices are Gorelo's agent assets; network items are its custom assets (Gorelo sync §6.2). */
export const ITEMS_TITLE: Record<ItemCategory, string> = { device: "Devices", network: "Network and printers" };
const EMPTY: Record<ItemCategory, string> = {
  device: "No devices yet. Computers with the Gorelo agent appear here.",
  network: "No network equipment or printers yet. Custom assets from Gorelo appear here.",
};
const FILTER = "Filter by name, manufacturer, model or serial number";

/** The Devices and the Network and printers tabs: a filterable table. Assets are made and removed in Gorelo. */
export function ClientItems({ clientId, category, site }: { clientId: string; category: ItemCategory; site?: string }) {
  const [text, setText] = useState("");
  const [siteId, setSiteId] = useState(site ?? "");
  const q = useDebounced(text.trim(), 250);
  const filter: ItemFilter = { category, ...(q ? { q } : {}), ...(siteId ? { siteId } : {}) };
  const { data: items, isLoading } = useItems(clientId, filter);
  const { data: sites = [] } = useSites(clientId);
  const filtered = Boolean(q || siteId);

  return (
    <Panel aria-label={ITEMS_TITLE[category]} title={ITEMS_TITLE[category]}>
      <div className="flex flex-wrap items-center gap-2">
        <label className="flex h-9 min-w-56 flex-1 items-center gap-1.5 rounded-md border border-line bg-surface px-2.5">
          <Search size={14} className="shrink-0 text-ink-3" aria-hidden="true" />
          <input
            aria-label={FILTER}
            placeholder={FILTER}
            value={text}
            onChange={(e) => setText(e.target.value)}
            className="min-w-0 flex-1 bg-transparent text-base outline-none placeholder:text-ink-3"
          />
        </label>
        <Select
          aria-label="Site"
          className="w-auto min-w-44"
          value={siteId}
          onChange={setSiteId}
          options={[{ value: "", label: "All sites" }, ...sites.map((s) => ({ value: s.id, label: s.name }))]}
        />
      </div>
      {isLoading ? <Spinner label="Loading…" /> : null}
      {items && items.length === 0 ? (
        <p className="py-6 text-center text-base text-ink-3">
          {filtered ? "Nothing matches these filters." : EMPTY[category]}
        </p>
      ) : null}
      {items && items.length > 0 ? (
        <ItemsTable clientId={clientId} items={items} label={ITEMS_TITLE[category]} />
      ) : null}
      {!isLoading && !items ? <EmptyState title="The list could not be loaded" /> : null}
    </Panel>
  );
}
